/**
 * Общие функции форматирования для локали ru-RU.
 * Используются фильтрами AngularJS и React-компонентами; ручное форматирование в компонентах недопустимо.
 */

export const LOCALE = 'ru-RU';
export const CURRENCY = 'RUB';

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateTimeSecondsFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const numberFormatter = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 2,
});
const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  minimumFractionDigits: 2,
});
const percentFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  maximumFractionDigits: 1,
});

type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Дата: 16.09.2026 */
export function formatDate(value: DateInput): string {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : '';
}

/** Дата и время: 16.09.2026 14:30 */
export function formatDateTime(value: DateInput): string {
  const date = toDate(value);
  return date ? dateTimeFormatter.format(date).replace(',', '') : '';
}

/** Дата и время с секундами: 16.09.2026 14:30:05 */
export function formatDateTimeSeconds(value: DateInput): string {
  const date = toDate(value);
  return date ? dateTimeSecondsFormatter.format(date).replace(',', '') : '';
}

/** Unix-время (секунды) → дата и время с секундами. */
export function formatUnixTimestamp(
  seconds: number | string | null | undefined
): string {
  if (seconds === null || seconds === undefined || seconds === '') {
    return '';
  }
  return formatDateTimeSeconds(Number(seconds) * 1000);
}

/** Число: 1 250 000,50 */
export function formatNumber(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : numberFormatter.format(num);
}

/** Валюта: 1 250 000,50 ₽ */
export function formatCurrency(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : currencyFormatter.format(num);
}

/** Проценты: 45,5 % (значение от 0 до 1) */
export function formatPercent(ratio: number): string {
  return percentFormatter.format(ratio);
}

const BYTE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ'];

/** Размер: 1,5 ГБ (десятичные единицы, как в исходном фильтре humansize) */
export function formatBytes(
  bytes: number | string | null | undefined,
  decimals = 1,
  base: 1000 | 1024 = 1000
): string {
  const num = Number(bytes);
  if (bytes === null || bytes === undefined || Number.isNaN(num)) {
    return '';
  }
  if (num === 0) {
    return `0 ${BYTE_UNITS[0]}`;
  }
  const exponent = Math.min(
    Math.floor(Math.log(Math.abs(num)) / Math.log(base)),
    BYTE_UNITS.length - 1
  );
  const value = num / base ** exponent;
  return `${new Intl.NumberFormat(LOCALE, {
    maximumFractionDigits: decimals,
  }).format(value)} ${BYTE_UNITS[exponent]}`;
}

/** Длительность в секундах → «2 ч 15 мин». */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const parts: string[] = [];
  if (days) parts.push(`${days} д`);
  if (hours) parts.push(`${hours} ч`);
  if (minutes) parts.push(`${minutes} мин`);
  if (!parts.length || (seconds && !days)) parts.push(`${seconds} с`);
  return parts.join(' ');
}

/** Склонение: plural(3, ['элемент', 'элемента', 'элементов']) → «элемента». */
export function plural(count: number, forms: [string, string, string]): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}
